/**
 * Browser chrome — navigation, address bar, tab row and console summary.
 *
 * Extracted from `BrowserPreviewPanel.tsx`. The individual controls were already
 * small and well-named; what they lacked was a file boundary, so the panel that
 * owned them could not be read or changed without reading all of them too.
 *
 * @module BrowserChrome
 */
import type {
  BrowserCommand,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  DevTunnelRecord,
} from "@cmmd-forge/contracts";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CameraIcon,
  EllipsisVerticalIcon,
  Loader2Icon,
  MousePointerClickIcon,
  PlusIcon,
  RotateCwIcon,
  XIcon,
} from "lucide-react";
import type { FormEvent, MutableRefObject, ReactNode } from "react";

import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../../components/ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../components/ui/tooltip";
import { resolveBrowserConsoleSummary } from "../../components/BrowserPreviewPanel.logic";
import type { BrowserCommentDraft } from "../../components/browser/browserCommentDraftStore";
import { BrowserAccessTargetBadge, BrowserAddressForm } from "./BrowserAddressBar";
import { BrowserChromeIconButton, CHROME_ICON_BUTTON_CLASS } from "./BrowserChromeButton";
import { BrowserRecordButton } from "./BrowserRecordButton";
import { BrowserPreviewConsoleSummaryRow } from "./BrowserConsoleRow";
import { BrowserPreviewTabsRow } from "./BrowserTabsRow";
import type {
  BrowserAddressSubmitter,
  BrowserOptionsMenuItem,
  BrowserPreviewAccessTarget,
  BrowserPreviewRuntimeState,
  BrowserPreviewSessionState,
} from "./browserShellTypes";

export interface BrowserChromeProps {
  readonly activeConsoleLogsMeta: BrowserPreviewSessionState["activeConsoleLogsMeta"];
  readonly activeConsoleSummary: ReturnType<typeof resolveBrowserConsoleSummary>;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly addressInputFocusedRef: MutableRefObject<boolean>;
  readonly addressInputRef: MutableRefObject<HTMLInputElement | null>;
  readonly addressValue: string;
  readonly browserAccessTarget: BrowserPreviewAccessTarget;
  readonly browserAddressBusy: boolean;
  readonly browserCommandBusy: boolean;
  readonly browserOptionsMenuItems: ReadonlyArray<BrowserOptionsMenuItem>;
  readonly commentDraft: BrowserCommentDraft;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly duplicateVisibleTabLabels: ReadonlySet<string>;
  readonly lifecycleAction: string | null;
  readonly onCaptureScreenshot: () => void | Promise<void>;
  readonly onClose?: (() => void) | undefined;
  readonly onNavigate: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  readonly onOpenExternal: (url: string) => void;
  readonly onOptionsAction: (id: BrowserOptionsMenuItem["id"]) => void | Promise<void>;
  readonly onRunCommand: (command: BrowserCommand) => void | Promise<unknown>;
  readonly onSetAddressInputFocused: (focused: boolean) => void;
  readonly onSetAddressValue: (value: string) => void;
  readonly onSubmitBrowserAddress: BrowserAddressSubmitter;
  readonly onToggleAnnotationMode: () => void;
  readonly pendingBrowserCommand: BrowserCommand["type"] | null;
  readonly pendingPointerAddressValueRef: MutableRefObject<string | null>;
  readonly previewVm: BrowserPreviewRuntimeState["previewVm"];
  readonly readBrowserAddressValue: () => string;
  readonly session: BrowserSessionSnapshot | null;
  readonly visibleTabLabels: ReadonlyArray<string>;
  readonly visibleTabs: ReadonlyArray<BrowserTabSnapshot>;
}

export function BrowserChrome(props: BrowserChromeProps) {
  return (
    <>
      <BrowserPreviewToolbar {...props} />
      {/*
       * Multi-session switcher intentionally removed: one browser session
       * per thread. If a worker spawns its own session, the server-side
       * openSession enforcement collapses it onto the thread's primary
       * session (see apps/server/src/browser/Layers/Manager.ts openSession).
       * Tabs (rendered below when session.tabs.length > 1) are how a user
       * juggles multiple pages WITHIN that single browser.
       */}
      <BrowserPreviewTabsRow {...props} />
      <BrowserPreviewConsoleSummaryRow {...props} />
    </>
  );
}

function BrowserPreviewToolbar(props: BrowserChromeProps) {
  return (
    <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border/60 px-2">
      <div className="flex items-center gap-0.5" role="group" aria-label="Navigation">
        <BrowserHistoryButton
          ariaLabel="Back"
          commandType="goBack"
          disabled={!props.activeTab?.canGoBack || props.browserCommandBusy}
          icon={<ArrowLeftIcon className="size-3.5" />}
          tabId={props.activeTab?.tabId ?? null}
          onRunCommand={props.onRunCommand}
        />
        <BrowserHistoryButton
          ariaLabel="Forward"
          commandType="goForward"
          disabled={!props.activeTab?.canGoForward || props.browserCommandBusy}
          icon={<ArrowRightIcon className="size-3.5" />}
          tabId={props.activeTab?.tabId ?? null}
          onRunCommand={props.onRunCommand}
        />
        <BrowserReloadButton
          activeTab={props.activeTab}
          browserCommandBusy={props.browserCommandBusy}
          onRunCommand={props.onRunCommand}
          pendingBrowserCommand={props.pendingBrowserCommand}
        />
      </div>
      <BrowserAccessTargetBadge target={props.browserAccessTarget} />
      <BrowserAddressForm {...props} />
      <BrowserAnnotationButton {...props} />
      <BrowserCaptureButton {...props} />
      <BrowserRecordButton session={props.session} activeTab={props.activeTab} />
      <BrowserNewTabButton {...props} />
      <BrowserCloseButton onClose={props.onClose} />
      <BrowserOptionsMenu
        lifecycleAction={props.lifecycleAction}
        menuItems={props.browserOptionsMenuItems}
        onOptionsAction={props.onOptionsAction}
      />
    </div>
  );
}

function BrowserHistoryButton(props: {
  readonly ariaLabel: string;
  readonly commandType: "goBack" | "goForward";
  readonly disabled: boolean;
  readonly icon: ReactNode;
  readonly onRunCommand: (command: BrowserCommand) => void | Promise<unknown>;
  readonly tabId: BrowserTabSnapshot["tabId"] | null;
}) {
  return (
    <BrowserChromeIconButton
      label={props.ariaLabel}
      icon={props.icon}
      disabled={props.disabled}
      onClick={() => {
        if (props.tabId) {
          void props.onRunCommand({ type: props.commandType, tabId: props.tabId });
        }
      }}
    />
  );
}

function BrowserReloadButton(props: {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly browserCommandBusy: boolean;
  readonly onRunCommand: (command: BrowserCommand) => void | Promise<unknown>;
  readonly pendingBrowserCommand: BrowserCommand["type"] | null;
}) {
  return (
    <BrowserChromeIconButton
      label="Reload"
      icon={
        props.pendingBrowserCommand === "reload" ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          <RotateCwIcon className="size-3.5" />
        )
      }
      disabled={!props.activeTab || props.browserCommandBusy}
      onClick={() => {
        if (props.activeTab) {
          void props.onRunCommand({ type: "reload", tabId: props.activeTab.tabId });
        }
      }}
    />
  );
}

function BrowserNewTabButton(props: BrowserChromeProps) {
  const busy =
    props.pendingBrowserCommand === "newTab" || props.pendingBrowserCommand === "navigate";
  return (
    <BrowserChromeIconButton
      label="Open tab"
      icon={
        busy ? <Loader2Icon className="size-3.5 animate-spin" /> : <PlusIcon className="size-3.5" />
      }
      disabled={props.browserAddressBusy}
      onPointerDown={() => {
        props.pendingPointerAddressValueRef.current = props.readBrowserAddressValue();
      }}
      onClick={() => {
        if (props.browserAddressBusy) {
          return;
        }
        const pointerAddressValue = props.pendingPointerAddressValueRef.current;
        props.pendingPointerAddressValueRef.current = null;
        void props.onSubmitBrowserAddress("new-tab", pointerAddressValue);
      }}
    />
  );
}

function BrowserCaptureButton(props: BrowserChromeProps) {
  return (
    <BrowserChromeIconButton
      label="Capture screenshot"
      icon={
        props.pendingBrowserCommand === "screenshot" ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          <CameraIcon className="size-3.5" />
        )
      }
      disabled={!props.previewVm.canCaptureScreenshot || props.browserCommandBusy}
      onClick={() => void props.onCaptureScreenshot()}
    />
  );
}

function BrowserAnnotationButton(props: BrowserChromeProps) {
  return (
    <BrowserChromeIconButton
      label="Select page area"
      icon={<MousePointerClickIcon className="size-3.5" />}
      className={props.commentDraft.selecting ? "bg-muted/80 text-foreground" : undefined}
      pressed={props.commentDraft.selecting}
      disabled={!props.previewVm.canAnnotate}
      onClick={props.onToggleAnnotationMode}
    />
  );
}

function BrowserCloseButton(props: { readonly onClose?: (() => void) | undefined }) {
  if (!props.onClose) {
    return null;
  }
  return (
    <BrowserChromeIconButton
      label="Close browser panel"
      icon={<XIcon className="size-3.5" />}
      onClick={props.onClose}
    />
  );
}

function BrowserOptionsMenu(props: {
  readonly lifecycleAction: string | null;
  readonly menuItems: ReadonlyArray<BrowserOptionsMenuItem>;
  readonly onOptionsAction: (id: BrowserOptionsMenuItem["id"]) => void | Promise<void>;
}) {
  return (
    <Menu>
      <Tooltip>
        <TooltipTrigger
          render={
            <MenuTrigger className={CHROME_ICON_BUTTON_CLASS} aria-label="Browser options">
              {props.lifecycleAction ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : (
                <EllipsisVerticalIcon className="size-3.5" />
              )}
            </MenuTrigger>
          }
        />
        <TooltipPopup side="bottom">Browser options</TooltipPopup>
      </Tooltip>
      <MenuPopup align="end" side="bottom">
        {props.menuItems.map((item) => (
          <MenuItem
            key={item.id}
            disabled={item.disabled}
            onClick={() => void props.onOptionsAction(item.id)}
          >
            {item.label}
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}
